import { useEffect, useMemo, useRef, useState } from "react";
import {
  type GoogleMapsAdvancedMarker,
  type GoogleMapsCircle,
  type GoogleMapsLatLng,
  type GoogleMapsLatLngLiteral,
  type GoogleMapsMap,
  type GoogleMapsMapClickEvent,
  type GoogleMapsMarkerMouseEvent,
  loadGoogleMapsApi,
} from "@/lib/google-maps";

type SiteLocationMapProps = {
  lat: number;
  lon: number;
  radius: number;
  onPositionChange: (coords: { lat: number; lon: number }) => void;
};

const DEFAULT_ERROR_MESSAGE =
  "Google Maps is unavailable. You can still enter coordinates manually.";
const DEFAULT_MAP_ID = "DEMO_MAP_ID";

// Google Maps JS API needs literal colors, not CSS tokens. Mirror the DS
// primary ramp (--color-primary-500 / --color-primary-200 in index.css).
const MAP_BRAND_COLOR = "#ff6a00";
const MAP_BRAND_FILL = "#ffd8bd";

function createMarkerPin(
  google: Awaited<ReturnType<typeof loadGoogleMapsApi>>
): HTMLElement {
  return new google.maps.marker.PinElement({
    background: MAP_BRAND_COLOR,
    borderColor: "#ffffff",
    glyphColor: "#ffffff",
    scale: 1,
  });
}

function toLatLngLiteral(
  position: GoogleMapsLatLng | GoogleMapsLatLngLiteral | null | undefined
): GoogleMapsLatLngLiteral | null {
  if (!position) {
    return null;
  }

  const maybeLatLng = position as GoogleMapsLatLng;
  if (typeof maybeLatLng.lat === "function") {
    return {
      lat: maybeLatLng.lat(),
      lng: maybeLatLng.lng(),
    };
  }

  return position as GoogleMapsLatLngLiteral;
}

function getMarkerEventPosition(
  marker: GoogleMapsAdvancedMarker,
  event?: GoogleMapsMarkerMouseEvent
): GoogleMapsLatLngLiteral | null {
  return toLatLngLiteral(event?.latLng ?? marker.position);
}

export default function SiteLocationMap({
  lat,
  lon,
  radius,
  onPositionChange,
}: SiteLocationMapProps) {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<GoogleMapsMap | null>(null);
  const markerRef = useRef<GoogleMapsAdvancedMarker | null>(null);
  const circleRef = useRef<GoogleMapsCircle | null>(null);
  const onPositionChangeRef = useRef(onPositionChange);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const center = useMemo(() => ({ lat, lng: lon }), [lat, lon]);
  const initialCenterRef = useRef(center);
  const initialRadiusRef = useRef(radius);

  useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    let cancelled = false;

    setIsLoading(true);
    setErrorMessage(null);

    void loadGoogleMapsApi(
      import.meta.env.VITE_GOOGLE_MAPS_API_KEY?.trim() || ""
    )
      .then((google) => {
        if (cancelled || !mapElementRef.current) {
          return;
        }

        if (!mapRef.current) {
          const map = new google.maps.Map(mapElementRef.current, {
            center: initialCenterRef.current,
            zoom: 17,
            clickableIcons: false,
            fullscreenControl: false,
            gestureHandling: "greedy",
            mapId:
              import.meta.env.VITE_GOOGLE_MAPS_MAP_ID?.trim() || DEFAULT_MAP_ID,
            mapTypeControl: false,
            streetViewControl: false,
          });

          const marker = new google.maps.marker.AdvancedMarkerElement({
            map,
            position: initialCenterRef.current,
            gmpDraggable: true,
            title: "Site location",
          });
          marker.replaceChildren(createMarkerPin(google));

          const circle = new google.maps.Circle({
            map,
            center: initialCenterRef.current,
            radius: initialRadiusRef.current,
            strokeColor: MAP_BRAND_COLOR,
            strokeOpacity: 0.95,
            strokeWeight: 2,
            fillColor: MAP_BRAND_FILL,
            fillOpacity: 0.25,
          });

          marker.addListener("drag", (event) => {
            const position = getMarkerEventPosition(marker, event);
            if (!position) {
              return;
            }

            circle.setCenter(position);
          });

          marker.addListener("dragend", (event) => {
            const position = getMarkerEventPosition(marker, event);
            if (!position) {
              return;
            }

            onPositionChangeRef.current({
              lat: position.lat,
              lon: position.lng,
            });
          });

          map.addListener("click", (event: GoogleMapsMapClickEvent) => {
            const position = event.latLng;
            if (!position) {
              return;
            }

            onPositionChangeRef.current({
              lat: position.lat(),
              lon: position.lng(),
            });
          });

          mapRef.current = map;
          markerRef.current = marker;
          circleRef.current = circle;
        }

        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }

        setIsLoading(false);
        setErrorMessage(
          error instanceof Error ? error.message : DEFAULT_ERROR_MESSAGE
        );
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !markerRef.current || !circleRef.current) {
      return;
    }

    mapRef.current.setCenter(center);
    markerRef.current.position = center;
    circleRef.current.setCenter(center);
    circleRef.current.setRadius(radius);
  }, [center, radius]);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        markerRef.current.map = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
    };
  }, []);

  if (typeof window === "undefined") {
    return null;
  }

  return (
    <div className="relative z-10 h-96 overflow-hidden rounded-lg border bg-muted">
      {errorMessage ? (
        <div className="flex h-full items-center justify-center p-4">
          <div className="max-w-sm space-y-2 text-center">
            <p className="font-medium text-foreground">Map unavailable</p>
            <p className="text-sm text-muted-foreground">{errorMessage}</p>
          </div>
        </div>
      ) : (
        <>
          <div ref={mapElementRef} className="h-full w-full" />
          {isLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-background/85 text-sm text-muted-foreground">
              Loading map...
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
